/**
 * App-side analytics wiring (Wave 1 · P2 [3/5], design D2/D3).
 *
 * Registers the app-owned context getters, the debug hook and the Snowplow
 * denylist into the `@heidi/runtime/analytics` pipeline at module scope.
 * Imported once from `_app.tsx`, before `markAnalyticsWiringComplete()` runs
 * there. The adapter registrations live at each SDK's own init site
 * (posthog-provider, amplitude-provider, tracing.ts, trackSnowplowEvent.ts, and
 * segment-boot.ts) — this module supplies enrichment
 * context, observability and destination policy.
 *
 * The getters are the SAME functions the legacy `trackEventV2` effect uses
 * (imported, not copied), so the two pipelines cannot drift while both are
 * live; the model's copies move here when the [4/5] shim deletes its
 * enrichment.
 */
import {
  registerAnalyticsContextProviders,
  registerAnalyticsDebugHook,
  registerSnowplowSuppressedEvents,
} from '@heidi/runtime/analytics';
// Reachability edge for the Snowplow adapter ([4/5]): its registration lives
// at module scope in trackSnowplowEvent.ts, which used to enter _app's graph
// only via the model's import — an edge the [5/N] codemod eventually deletes.
// This explicit side-effect import keeps the adapter registered regardless.
import '@/lib/snowplow/trackSnowplowEvent';
// Same reachability edge for the `CurrentIds` port: the app registers its
// adapter at module scope in useCurrentIds.ts, and this is the short static
// path from _app.tsx to it. Without it the port reads '' app-wide.
import '@/hooks/useCurrentIds';
import { browserName, deviceType, osName } from 'react-device-detect';

import { getDeploymentInventoryProps } from '@/lib/analytics/deployment-inventory';
import {
  getAppType,
  getInstallSourceAnalyticsTraits,
  getPlatform,
} from '@/lib/analytics/legacy-sender-context';
import { getSessionLayoutVersion } from '@/lib/analytics/session-layout-context';
import { SNOWPLOW_SUPPRESSED_EVENTS } from '@/lib/analytics/snowplow-suppression';
import { getChronicleDeviceProps } from '@/utils/analytics/chronicle-device-props';
import { logTrackedAnalyticsEvent } from '@/utils/analytics/debugger';
import { getDesktopInstallerType } from '@/utils/appConfiguration';
import { getAppVersionTraits } from '@/utils/appVersion';
import { getCurrentSessionId } from '@heidi/runtime/shell';

registerAnalyticsContextProviders({
  getSessionId: getCurrentSessionId,
  getSessionLayoutVersion,
  // Mirrors the legacy effect's system-identity bag (spread FIRST, so caller
  // props may override — e.g. `device_type`); version traits ride here too.
  getAppTraits: () => ({
    device_type: deviceType,
    app_type: getAppType(),
    client_platform: getAppType(),
    platform: getPlatform(),
    ...getAppVersionTraits(),
    os_name: osName,
    browser_name: browserName,
  }),
  getInstallerType: getDesktopInstallerType,
  getDeploymentProps: getDeploymentInventoryProps,
  getInstallSourceProps: getInstallSourceAnalyticsTraits,
  getChronicleDeviceProps,
});

registerAnalyticsDebugHook(logTrackedAnalyticsEvent);

// Destination-routing policy: which event names never reach Snowplow. The
// package holds the mechanism, this app holds the list — an event name is a
// warehouse decision, not runtime behaviour. Registering it here is what makes
// the policy non-positional: it applies to every emit that reaches the
// pipeline, whichever facade the call site imported.
registerSnowplowSuppressedEvents(SNOWPLOW_SUPPRESSED_EVENTS);
